import axios from 'axios';

import { createAsyncThunk } from '@reduxjs/toolkit';
import { availableCandidatesStoreKey } from './availableCandidates.const';

import {
  getAvailableCandidatesAPI,
  getAvailableCandidatesCountAPI,
  postAvailableCandidateInterviewRequestAPI,
  postAvailableCandidateLearnMoreAPI,
  postAvailableCandidateMarkAPI,
} from '@constants';
import { RootState } from '@redux/hooks';
import {
  AvailableCandidateMark,
  IAvailableCandidate,
  IAvailableCandidateInterviewRequest,
} from '@types';

export const fetchAvailableCandidates = createAsyncThunk(
  `${availableCandidatesStoreKey}/fetchAvailableCandidates`,
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get<IAvailableCandidate[]>(
        getAvailableCandidatesAPI,
        { withCredentials: true },
      );

      return response.data;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  },
);

export const fetchAvailableCandidatesCount = createAsyncThunk(
  `${availableCandidatesStoreKey}/fetchAvailableCandidatesCount`,
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get<{ count: number }>(
        getAvailableCandidatesCountAPI,
        { withCredentials: true },
      );

      return response.data;
    } catch (error: any) {
      if (!error.response) {
        throw error;
      }

      return rejectWithValue(error.response.data);
    }
  },
);

const postAvailableCandidateRequest = <Body extends object>(
  name: string,
  url: (candidateId: string) => string,
) =>
  createAsyncThunk(
    `${availableCandidatesStoreKey}/${name}`,
    async (
      { candidateId, ...data }: { candidateId: string } & Body,
      { dispatch, rejectWithValue },
    ) => {
      const refresh = () => {
        dispatch(fetchAvailableCandidates());
        dispatch(fetchAvailableCandidatesCount());
      };

      try {
        await axios.post(url(candidateId), data, { withCredentials: true });
        refresh();

        return true;
      } catch (error: any) {
        if (!error.response) {
          throw error;
        }

        if ([404, 409].includes(error.response.status)) {
          refresh();
          return false;
        }

        return rejectWithValue(error.response.data);
      }
    },
    {
      condition: ({ candidateId }, { getState }) =>
        !(getState() as RootState)[
          availableCandidatesStoreKey
        ].writing.includes(candidateId),
    },
  );

export const requestAvailableCandidateInterview =
  postAvailableCandidateRequest<IAvailableCandidateInterviewRequest>(
    'requestInterview',
    postAvailableCandidateInterviewRequestAPI,
  );

export const requestAvailableCandidateLearnMore =
  postAvailableCandidateRequest<{
    text: string;
  }>('requestLearnMore', postAvailableCandidateLearnMoreAPI);

export const markAvailableCandidate = postAvailableCandidateRequest<{
  mark: AvailableCandidateMark | null;
}>('mark', postAvailableCandidateMarkAPI);
